import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiGet, apiPost, isBackendConfigured } from '../utils/backendApi';
import { OrgAgree } from './Clickwrap';
import { legalStatus } from '../data/legal';
import { friendlyError, useEntityList } from '../data/entities';
import { PHASES, LAB_SECTIONS, FREE_PHASES, ACTIVE, tellNova, useAct, useLabParams } from './lab/shared';
import Concept from './lab/Concept';
import Design from './lab/Design';
import Surfaces from './lab/Surfaces';
import Try from './lab/Try';
import Research from './lab/Research';
import Ideas from './lab/Ideas';
import Stories from './lab/Stories';
import Refine from './lab/Refine';
import Test from './lab/Test';
import Perf from './lab/Perf';
import Map from './lab/Map';
import Solution from './lab/Solution';

// Spoken notes name the site, never the URL.
const hostOf = (u) => { try { return new URL(u).hostname.replace(/^www\./, ''); } catch { return ''; } };

// Lab — the studio (decision lab-is-the-studio, 2026-10-14; before that
// App Builder's workbench, [[lab-system]]): one screen per phase, in three
// rail sections — Think (Concept, the voice-guided studio [[studio-journey]]
// → Research → Ideas → Stories), Design (Design → Refine → Surfaces → Try)
// and Prove (Test → Performance → Integration map). Design, Surfaces and
// Try are the app itself and never sit behind the add-on; the other phases
// run through /lab (api/labApi.js) under the Lab add-on; runs (labRun) and
// artifacts (labArtifact: concepts, ideas, stories) stream in through
// Firestore. `studio` carries App Builder's records and permissions into
// the Design-section phases. URL state: ?tab=lab&phase=<id>&id=<record>
// &node=<map node>&fig=<figure>&screen=<concept screen>&revise=1&card=<design card>&present=1.

export default function Lab({ org, app, isAdmin, canEdit, canApply = canEdit, canApprove = isAdmin, canCredentials = canEdit, canBuild = canApply, onTab, ops, studio = null, defaultPhase = 'design' }) {
  const [info, setInfo] = useState(null);
  const [loadErr, setLoadErr] = useState('');
  const [payDocs, setPayDocs] = useState(null);
  const { phase, id, node, fig, screen, card, present, revise, setPhase, setId, setNode, setFig, setScreen, setPresent, setRevise, go } = useLabParams(defaultPhase);
  const { busy, err, act } = useAct();
  const { docs: allRuns } = useEntityList('labRun', { org: org.id });
  const { docs: allArts } = useEntityList('labArtifact', { org: org.id });
  const runs = (allRuns || []).filter((r) => r.appId === app.id).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  const artifacts = (allArts || []).filter((a) => a.appId === app.id).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

  const load = useCallback(async () => {
    if (!isBackendConfigured()) return;
    try { setInfo(await apiGet(`/lab/${org.id}/apps/${app.id}`)); setLoadErr(''); } catch (ex) { setLoadErr(friendlyError(ex)); }
  }, [org.id, app.id]);
  useEffect(() => { load(); }, [load]);
  const activeCount = runs.filter((r) => ACTIVE.has(r.status)).length;
  useEffect(() => { if (!activeCount) load(); }, [activeCount, load]); // usage counters move when runs finish

  // The live voice session follows the browser ([[studio-journey]]): one
  // BROWSING note per visit (NoVA narrates it), one when the run ends.
  // Whatever Lab phase is on screen — the note is about the run, not the pane.
  const toldRef = useRef(new Set());
  useEffect(() => {
    for (const r of runs) {
      // Research runs: one note per finished search agent (what it found,
      // how many so far, time left) and one when the brief is ready — the
      // user hears progress instead of waiting in silence.
      if (r.kind === 'research') {
        if (ACTIVE.has(r.status) && r.live?.total) {
          const key = `${r.id}:research:${r.live.done || 0}`;
          if (!toldRef.current.has(key) && (r.live.done || 0) > 0) {
            toldRef.current.add(key);
            const cards = Object.values(r.live.searches || {}).filter((c) => c.status === 'done' || c.status === 'failed');
            const last = cards.sort((a, b) => (b.doneAt || 0) - (a.doneAt || 0))[0];
            const etaSec = info?.eta?.research; const elapsed = (Date.now() - (r.startedAt || r.createdAt || Date.now())) / 1000;
            const left = etaSec ? Math.max(0, Math.round((etaSec - elapsed) / 10) * 10) : null;
            tellNova(`RESEARCH — search ${r.live.done} of ${r.live.total} finished: "${last?.q || ''}" → ${last?.count || 0} sources${(last?.sources || []).length ? ` (e.g. ${last.sources.slice(0, 2).map((s) => s.title).filter(Boolean).join('; ')})` : ''}. ${r.live.sources || 0} sources so far${left != null ? `, about ${left < 15 ? 'a few seconds' : `${Math.round(left / 60) || 1} minute${left >= 90 ? 's' : ''}`} left` : ''}.`, { respond: true, runId: r.id });
          }
        } else if (!ACTIVE.has(r.status) && r.finishedAt && Date.now() - r.finishedAt < 120000) {
          const key = `${r.id}:end`;
          if (toldRef.current.has(key)) continue;
          toldRef.current.add(key);
          tellNova(r.status === 'done'
            ? `RESEARCH FINISHED — ${(r.findings?.queries || []).length} searches, ${(r.findings?.sources || []).length} sources, ${(r.proposals || []).length} proposals. Brief: ${String(r.findings?.markdown || '').slice(0, 1000)}`
            : `RESEARCH STOPPED (${r.status})${r.error ? `: ${String(r.error).slice(0, 200)}` : ''}.`, { respond: true, runId: '' });
        }
        continue;
      }
      if (r.kind !== 'browse') continue;
      if (ACTIVE.has(r.status) && r.live?.url) {
        const key = `${r.id}:${r.live.step}:${r.live.url}:${r.live.keyframeKey ? 'k' : ''}`;
        if (toldRef.current.has(key) || !r.live.keyframeKey) continue; // narrate once the page has rendered
        toldRef.current.add(key);
        // Speakable facts only: the realtime model has read these notes
        // aloud verbatim, so no run ids, no URLs, no stage directions here
        // (what to do with notes is in the voice prompt — CONSOLE NOTES).
        tellNova(`BROWSING — page ${r.live.step || 0} of ${r.live.of || '?'}: "${r.live.title || ''}" (${hostOf(r.live.url)}). ${r.live.caption || ''}`, { respond: true, runId: r.id });
      } else if (!ACTIVE.has(r.status) && r.finishedAt && Date.now() - r.finishedAt < 120000) {
        const key = `${r.id}:end`;
        if (toldRef.current.has(key)) continue;
        toldRef.current.add(key);
        const n = (r.findings?.visits || []).filter((v) => !v.failed).length;
        tellNova(r.status === 'done'
          ? `BROWSING FINISHED — ${n} sites read. Summary: ${String(r.findings?.summary || '').slice(0, 1200)}`
          : `BROWSING STOPPED (${r.status}) — ${n} sites were read before it stopped; what was found is kept.`, { respond: true, runId: '' });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runs]);

  const free = FREE_PHASES.has(phase);
  if (!isBackendConfigured() && !free) return <p className="cs-help">The backend API is not configured in this build.</p>;
  if (!info && !free) return loadErr ? <p className="cs-form-error">{loadErr}</p> : <p className="mf-muted">Loading…</p>;

  const entitled = !!info?.entitled;
  const usage = info?.usage || {};
  const inc = usage.included || {};
  const counts = {
    ideas: artifacts.filter((a) => a.kind === 'idea' && a.status === 'proposed').length,
    stories: artifacts.filter((a) => a.kind === 'story' && (a.status === 'accepted' || (a.status === 'tested' && a.links?.lastVerdict === 'fail'))).length,
    test: runs.filter((r) => r.kind === 'test' && r.status === 'awaiting_approval').length,
    perf: runs.find((r) => r.kind === 'perf' && r.status === 'done')?.findings?.verdict === 'fail' ? 1 : 0,
    research: runs.filter((r) => (r.kind === 'research' || r.kind === 'browse') && ACTIVE.has(r.status)).length,
    concept: runs.filter((r) => (r.kind === 'concept' || r.kind === 'visuals') && ACTIVE.has(r.status)).length || artifacts.filter((a) => a.kind === 'concept' && a.status === 'proposed').length,
    refine: runs.filter((r) => r.kind === 'refine' && ACTIVE.has(r.status)).length,
  };
  const urgent = { test: true };
  const agents = ops?.data?.agents || [];
  const onPhase = (p, recId = null, extra = {}) => go(p, recId, extra);
  const common = { org, app, agents, runs, artifacts, info, reloadInfo: load, sel: id, setSel: setId, canEdit: canEdit && entitled, canApply: canApply && entitled, canApprove, canCredentials, canBuild, isAdmin, busy, act, onTab, onPhase };
  const studioProps = { org, app, studio, onPhase };
  // A gated phase without the add-on shows the upsell in the canvas; the
  // rail itself always lists every phase so the shape of the work is visible.
  const gated = !free && !entitled;

  return (
    <div className={`lab-wb ${phase === 'ideas' || phase === 'map' || phase === 'solution' ? 'is-wide' : ''} ${FREE_PHASES.has(phase) ? 'is-studio' : ''} ${phase === 'concept' && present ? 'is-present' : ''}`}>
      <nav className="lab-rail" aria-label="Lab phases">
        {LAB_SECTIONS.map((sec) => (
          <div key={sec.id} className="lab-rail-sec">
            <div className="lab-rail-sec-h">{sec.label}</div>
            {PHASES.filter((p) => p.section === sec.id).map((p) => (
              <button key={p.id} type="button" className={`lab-rail-btn ${phase === p.id ? 'is-on' : ''} ${!p.free && !entitled ? 'is-addon' : ''}`} onClick={() => setPhase(p.id)} aria-current={phase === p.id ? 'page' : undefined} title={p.blurb}>
                <span className="lab-rail-n">{PHASES.indexOf(p) + 1}</span>
                <span className="lab-rail-label">{p.label}</span>
                {counts[p.id] ? <span className={`lab-rail-badge ${urgent[p.id] ? 'is-urgent' : ''}`}>{counts[p.id]}{urgent[p.id] ? '!' : ''}</span> : null}
              </button>
            ))}
          </div>
        ))}
        {info && entitled && (
          <div className="lab-rail-usage mf-muted">
            <span>{usage.researchRuns ?? 0}/{inc.researchRuns ?? 0} research runs</span>
            <span>{usage.browserMinutes ?? 0}/{inc.browserMinutes ?? 0} browser min</span>
            <span>{usage.images ?? 0}/{inc.images ?? 0} images</span>
            <span>{usage.period}</span>
          </div>
        )}
        {info && !entitled && <p className="lab-rail-usage mf-muted"><span>Think and Prove phases: {info.addon?.label || 'Lab add-on'}{info.addon?.priceLabel ? ` · ${info.addon.priceLabel}` : ''}</span></p>}
      </nav>
      <div
        className="lab-canvas"
        data-nova-note={`Lab phase ${PHASES.findIndex((p) => p.id === phase) + 1} of ${PHASES.length}: ${PHASES.find((p) => p.id === phase)?.label || phase} — ${PHASES.find((p) => p.id === phase)?.blurb || ''}${gated ? ' (add-on not active: upsell shown)' : ''}${id ? `, record ${id} selected` : ''}. App "${app.name}": ${counts.ideas} ideas proposed, ${counts.stories} stories to prove, ${counts.test} test runs awaiting approval${counts.research ? `, research running` : ''}.`.slice(0, 320)}
      >
        {err && <p className="cs-form-error lab-err" role="alert">{err}</p>}
        {gated && info && (
          <div className="lab-upsell">
            <div>
              <strong>{info.addon.label}</strong>{info.addon.priceLabel ? <span className="mf-muted"> · {info.addon.priceLabel}</span> : null}
              <p className="cs-help" style={{ margin: '4px 0 0' }}>{info.addon.blurb || 'Research the market, refine the design and test the hosted app in a real browser — results flow back into this app and its instance.'}</p>
              <p className="mf-muted" style={{ margin: '4px 0 0', fontSize: 12 }}>Includes {inc.researchRuns ?? inc.labResearchRuns ?? 0} research runs and {inc.browserMinutes ?? inc.labBrowserMinutes ?? 0} browser minutes a month for the whole organization; more is metered. Design, Surfaces and Try are always yours.</p>
            </div>
            {isAdmin ? (
              <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" disabled={busy === 'buy'} onClick={() => act('buy', async () => {
                const status = await legalStatus(org.id);
                if (!status.orgOk) { setPayDocs(status.agreements || []); return; }
                const out = await apiPost('/billing/checkout', { orgId: org.id, addonKey: 'lab' });
                if (out?.url) window.location.assign(out.url); else await load();
              })}>{busy === 'buy' ? 'Starting…' : `Add ${info.addon.label}`}</button>
            ) : <span className="cs-help">An organization admin can add it.</span>}
            {payDocs && <OrgAgree orgId={org.id} docs={payDocs} onDone={() => act('buy', async () => {
              const out = await apiPost('/billing/checkout', { orgId: org.id, addonKey: 'lab' });
              if (out?.url) window.location.assign(out.url); else await load();
            })} />}
            <div className="lab-detail" style={{ marginTop: 12 }}><h3 style={{ margin: 0 }}>What the add-on phases do</h3><ul className="mf-list">{PHASES.filter((p) => !p.free).map((p) => <li key={p.id} className="mf-row"><strong style={{ minWidth: 120 }}>{p.label}</strong><span className="mf-muted">{p.blurb}</span></li>)}</ul></div>
          </div>
        )}
        {gated && !info && (loadErr ? <p className="cs-form-error">{loadErr}</p> : <p className="mf-muted">Loading…</p>)}
        {phase === 'design' && (studio ? <Design {...studioProps} card={card} /> : <p className="cs-help">Open this phase from App Builder.</p>)}
        {phase === 'surfaces' && (studio ? <Surfaces {...studioProps} /> : <p className="cs-help">Open this phase from App Builder.</p>)}
        {phase === 'try' && (studio ? <Try {...studioProps} /> : <p className="cs-help">Open this phase from App Builder.</p>)}
        {entitled && phase === 'concept' && <Concept {...common} fig={fig} setFig={setFig} screen={screen} setScreen={setScreen} revise={revise} setRevise={setRevise} present={present} setPresent={setPresent} dev={ops?.data?.dev || null} reloadOps={ops?.reload} />}
        {entitled && phase === 'research' && <Research {...common} />}
        {entitled && phase === 'ideas' && <Ideas {...common} />}
        {entitled && phase === 'stories' && <Stories {...common} />}
        {entitled && phase === 'refine' && <Refine {...common} />}
        {entitled && phase === 'test' && <Test {...common} />}
        {entitled && phase === 'perf' && <Perf {...common} />}
        {entitled && phase === 'map' && <Map {...common} ops={ops} node={node} setNode={setNode} />}
        {entitled && phase === 'solution' && <Solution {...common} />}
      </div>
    </div>
  );
}
